import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import { Calendar, User, ArrowLeft, Download, BookOpen, Clock, Users, Share2 } from 'lucide-react';

const DEFAULT_META_DESCRIPTION = 'Peťo Sloboda - Gramotnosť ako zbraň';

const BlogPost = () => {
    const { slug } = useParams();
    const navigate = useNavigate();

    // Mock data for the technical structure - in a real app this would come from a CMS/API
    const post = blogPosts.find(p => p.slug === slug);

    useEffect(() => {
        if (!post) return undefined;
        const meta = document.querySelector('meta[name="description"]');
        const previousTitle = document.title;
        const previousDescription = meta?.getAttribute('content') ?? '';

        document.title = post.seoTitle || `${post.title} | Peťo Sloboda`;
        if (post.metaDescription && meta) {
            meta.setAttribute('content', post.metaDescription);
        }

        return () => {
            document.title = previousTitle;
            if (meta) meta.setAttribute('content', previousDescription || DEFAULT_META_DESCRIPTION);
        };
    }, [post]);

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 text-gray-400 font-bold uppercase tracking-widest text-sm">
                Článok nebol nájdený
            </div>
        );
    }

    return (
        <div className="blog-post-page min-h-screen bg-white font-body text-brand-dark-blue overflow-x-clip">

            {/* 1. ARTICLE HEADER / HERO */}
            <div className="bg-gray-50 py-16 lg:py-24 border-b border-gray-100 relative overflow-x-clip">
                <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '400px', backgroundRepeat: 'repeat' }} />

                <div className="container mx-auto px-4 sm:px-5 relative z-10 max-w-full min-w-0">
                    <div className="max-w-4xl mx-auto min-w-0">
                        <Link to="/blog" className="inline-flex items-center text-brand-orange font-bold mb-10 hover:-translate-x-2 transition-transform group">
                            <ArrowLeft size={20} className="mr-2" /> Späť na prehľad článkov
                        </Link>

                        <div className="inline-block bg-brand-orange/10 text-brand-orange px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest mb-8 shadow-sm">
                            {post.category}
                        </div>

                        <h1 className="text-2xl min-[400px]:text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-heading font-bold mb-10 leading-[1.1] text-brand-dark-blue break-words">
                            {post.title}
                        </h1>

                        <div className="flex flex-wrap items-center gap-y-4 gap-x-10 text-sm font-bold text-gray-400 uppercase tracking-widest">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-brand-dark-blue rounded-full flex items-center justify-center text-white">PS</div>
                                <span>{post.author}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar size={18} className="text-brand-orange" />
                                {post.date}
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock size={18} className="text-brand-orange" />
                                {post.readTime} na čítanie
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 2. ARTICLE CONTENT SECTION */}
            <div className="container mx-auto px-4 sm:px-5 py-16 lg:py-24 max-w-full min-w-0">
                <article className="max-w-4xl mx-auto min-w-0">

                    {/* Featured Media */}
                    {post.youtubeId ? (
                        <div className="mb-16 aspect-video rounded-[2.5rem] overflow-hidden shadow-2xl bg-black border border-gray-100 group">
                            <iframe
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${post.youtubeId}?autoplay=0&rel=0`}
                                title="Expertné video k téme"
                                frameBorder="0"
                                allowFullScreen
                            />
                        </div>
                    ) : (
                        <div className="mb-16 rounded-2xl sm:rounded-[2.5rem] overflow-hidden shadow-2xl h-[min(55vh,400px)] sm:h-[400px]">
                            <img src={post.image} className="w-full h-full object-cover" alt={post.title} />
                        </div>
                    )}

                    {/* Rich Text Content */}
                    <div
                        className="prose prose-lg lg:prose-xl max-w-none text-gray-700 mb-20 break-words
                        prose-headings:font-heading prose-headings:text-brand-dark-blue 
                        prose-p:leading-relaxed prose-strong:text-brand-dark-blue prose-strong:font-bold"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* Social Share / Meta */}
                    <div className="pt-10 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6">
                        <div className="flex items-center gap-4">
                            <span className="text-gray-400 text-xs font-bold uppercase tracking-widest">Zdieľať vedomosti:</span>
                            <div className="flex gap-2">
                                <button className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-brand-orange hover:text-white transition-all"><Share2 size={18} /></button>
                            </div>
                        </div>
                        <Link to="/blog" className="text-brand-dark-blue font-bold hover:text-brand-orange transition-colors">
                            ← Iné témy na blogu
                        </Link>
                    </div>
                </article>
            </div>

            {/* 3. UNIFIED DUAL CTA - SITEPART CONSISTENCY */}
            <section className="py-24 bg-brand-dark-blue text-white relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url(/symbol-3.svg)', backgroundSize: '300px', backgroundRepeat: 'repeat' }} />
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-5xl mx-auto flex flex-col items-center text-center mb-16">
                        <h2 className="text-3xl lg:text-5xl font-heading font-bold mb-6">Páčilo sa vám čítanie?</h2>
                        <p className="text-xl text-gray-300">Posuňte svoje znalosti o úroveň vyššie a prejdite od teórie k činom.</p>
                    </div>

                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
                        {/* Book CTA */}
                        <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-500">
                            <div className="w-16 h-16 bg-brand-orange/20 rounded-2xl flex items-center justify-center text-brand-orange mb-8 group-hover:scale-110 transition-transform">
                                <Download size={32} />
                            </div>
                            <h2 className="text-3xl font-heading font-bold mb-4 leading-tight">Stiahnuť knihu Renta bez dátumu spotreby zadarmo</h2>
                            <p className="text-gray-300 mb-8 max-w-sm">Pochopte pravidlá hry a prečítajte si ucelenú mapu k vášmu majetkovému pokoju.</p>
                            <button
                                onClick={() => navigate('/kniha/objednavka')}
                                className="w-full bg-brand-orange hover:bg-red-600 text-white font-bold py-4 rounded-xl shadow-xl transition-all text-lg flex items-center justify-center gap-2"
                            >
                                <Download size={20} /> Stiahnuť zdarma
                            </button>
                        </div>

                        {/* Consultation CTA */}
                        <div className="bg-white p-10 lg:p-14 rounded-[3rem] flex flex-col items-center text-center group hover:shadow-2xl transition-all duration-500">
                            <div className="w-16 h-16 bg-brand-dark-blue/5 rounded-2xl flex items-center justify-center text-brand-dark-blue mb-8 group-hover:scale-110 transition-transform">
                                <Users size={32} />
                            </div>
                            <h2 className="text-3xl font-heading font-bold text-brand-dark-blue mb-4">Rezervovať nezáväznú diagnostiku</h2>
                            <p className="text-gray-500 mb-8 max-w-sm">Ak už teóriu ovládate a hľadáte architekta majetku, ktorý vám pomôže nastaviť systém, poďme sa pozrieť na vaše čísla. Žiadne tlačenie do produktov. Len otvorená diskusia o vašej stratégii.</p>
                            <Link
                                to="/konzultace"
                                className="w-full border-2 border-brand-dark-blue text-brand-dark-blue hover:bg-brand-dark-blue hover:text-white font-bold py-4 rounded-xl transition-all text-lg flex items-center justify-center gap-2"
                            >
                                <Calendar size={20} /> Bezplatná konzultácia
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default BlogPost;
